import { type ReactNode, isValidElement, useEffect, useState } from "react";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import {
  type CompanySettings,
  type CompanySettingsPatch,
  type WarmupSpeed,
  useCompanySettings,
  useUpdateCompanySettings,
} from "@/lib/companySettings";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { SimpleSelect, type SimpleSelectOption } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { pushToast } from "@/components/Toaster";

/* ------------------------------------------------------------ shared helpers */

function useSectionDraft<K extends keyof CompanySettings>(group: K) {
  const { data } = useCompanySettings();
  const update = useUpdateCompanySettings();
  const [draft, setDraft] = useState<CompanySettings[K] | null>(null);

  // (Re)seed when the server object changes — initial load + after a save
  // (onSuccess writes the canonical result back into the cache).
  useEffect(() => {
    if (data) setDraft(data[group]);
  }, [data, group]);

  const set = (patch: Partial<CompanySettings[K]>) =>
    setDraft((d) => (d ? { ...d, ...patch } : d));

  const dirty =
    !!data && !!draft && JSON.stringify(draft) !== JSON.stringify(data[group]);

  const save = async () => {
    if (!draft) return;
    try {
      await update.mutateAsync({ [group]: draft } as CompanySettingsPatch);
      pushToast({ kind: "success", text: "Настройки сохранены" });
    } catch (e) {
      pushToast({
        kind: "error",
        text: e instanceof ApiError ? e.detail : "Не удалось сохранить",
      });
    }
  };

  return { draft, set, save, dirty, saving: update.isPending } as const;
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  // On a phone the fixed 208px control column left ~70px for the label, which
  // then wrapped one word per line. A switch is small enough to stay beside
  // its label; an input or a select drops below it and takes the full width.
  const compact = isValidElement(children) && children.type === Switch;
  return (
    <div
      className={cn(
        "flex gap-x-4 gap-y-2 border-b border-border py-3 last:border-0",
        compact
          ? "items-start justify-between"
          : "flex-col sm:flex-row sm:items-start sm:justify-between",
      )}
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-fg-primary">{label}</p>
        {hint && <p className="mt-0.5 text-xs text-fg-muted">{hint}</p>}
      </div>
      <div
        className={cn(
          "flex items-center sm:w-52 sm:shrink-0 sm:justify-end",
          compact ? "shrink-0 justify-end" : "w-full",
        )}
      >
        {children}
      </div>
    </div>
  );
}

function SaveBar({ dirty, saving, onSave }: { dirty: boolean; saving: boolean; onSave: () => void }) {
  return (
    <div className="flex justify-end pt-2">
      <Button size="sm" disabled={!dirty} loading={saving} onClick={onSave}>
        Сохранить
      </Button>
    </div>
  );
}

/**
 * Number <input> bound to a possibly-null numeric field. "" → null.
 * `min`/`max` mirror the server's range and are applied when the field loses
 * focus (not per keystroke, or "50" could not be typed past a min of 10) — a
 * value outside the range used to fail the whole card's save.
 */
function NumField({
  value,
  onChange,
  placeholder,
  min,
  max,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  placeholder?: string;
  min?: number;
  max?: number;
}) {
  return (
    <Input
      inputMode="numeric"
      value={value == null ? "" : String(value)}
      placeholder={placeholder}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, "");
        onChange(digits === "" ? null : Number(digits));
      }}
      onBlur={() => {
        if (value == null) return;
        const clamped = Math.min(max ?? value, Math.max(min ?? value, value));
        if (clamped !== value) onChange(clamped);
      }}
      className="text-right"
    />
  );
}

function SectionCard({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {subtitle && <p className="text-sm text-fg-muted">{subtitle}</p>}
      </CardHeader>
      <CardContent className="space-y-1">{children}</CardContent>
    </Card>
  );
}


/* ----------------------------------------------------------------- General */

// Common IANA zones for the operator's likely regions; free-text is allowed too.
const TZ_OPTS: SimpleSelectOption[] = [
  "UTC",
  "Europe/Moscow",
  "Europe/Kyiv",
  "Europe/Warsaw",
  "Europe/London",
  "Asia/Almaty",
  "Asia/Tbilisi",
  "Asia/Dubai",
  "Asia/Bangkok",
  "America/New_York",
].map((z) => ({ value: z, label: z }));

export function GeneralSettingsCard() {
  const { draft, set, save, dirty, saving } = useSectionDraft("general");
  if (!draft) return null;
  return (
    <SectionCard title="Общие" subtitle="Часовой пояс компании.">
      <Row
        label="Часовой пояс"
        hint="Окно «тихих часов» рассылки считается в этом поясе. На отображение дат в интерфейсе не влияет (оно идёт по языку UI — переключатель RU/EN в шапке)."
      >
        <SimpleSelect
          options={TZ_OPTS}
          value={draft.timezone}
          onValueChange={(v) => set({ timezone: v })}
        />
      </Row>
      <SaveBar dirty={dirty} saving={saving} onSave={save} />
    </SectionCard>
  );
}

/* ------------------------------------------------------------------ Warmup */

const SPEED_OPTS: SimpleSelectOption[] = [
  { value: "auto", label: "Авто (по возрасту)" },
  { value: "natural", label: "Естественный" },
  { value: "fast", label: "Быстрый" },
  { value: "aggressive", label: "Агрессивный" },
];

const ECONOMY_OPTS: SimpleSelectOption[] = [
  { value: "auto", label: "Авто (эконом на mobile/residential)" },
  { value: "off", label: "Качать всегда (макс. реализм)" },
  { value: "on", label: "Не качать (эконом везде)" },
];

export function WarmupSettingsCard() {
  const { draft, set, save, dirty, saving } = useSectionDraft("warmup");
  if (!draft) return null;
  return (
    <SectionCard title="Прогрев по умолчанию" subtitle="Применяется к новым аккаунтам.">
      <Row
        label="Авто-старт прогрева при импорте"
        hint="Как стоит переключатель «Сразу запустить прогрев» в окне импорта. Без прогрева не запланируется и «Защита от продавца» — она выполняется его шагами."
      >
        <Switch
          checked={draft.auto_start_on_import}
          onCheckedChange={(v) => set({ auto_start_on_import: !!v })}
        />
      </Row>
      <Row
        label="Скачивание медиа в прогреве"
        hint="«Как настоящий клиент» (фото/стикеры/голосовые) против экономии трафика на платных mobile/residential прокси."
      >
        <SimpleSelect
          options={ECONOMY_OPTS}
          value={draft.economy_mode}
          onValueChange={(v) => set({ economy_mode: v as "auto" | "on" | "off" })}
        />
      </Row>
      <Row
        label="Вступлений в день (макс., 0–50)"
        hint="Жёсткий потолок вступлений в каналы за день. Действует на прогревы, запланированные после сохранения. Пусто — авто по пресету/возрасту."
      >
        <NumField
          value={draft.max_joins_per_day}
          onChange={(v) => set({ max_joins_per_day: v })}
          min={0}
          max={50}
        />
      </Row>
      <SaveBar dirty={dirty} saving={saving} onSave={save} />
    </SectionCard>
  );
}

/* ------------------------------------------------------------------ Import */

export function ImportSettingsCard() {
  const { draft, set, save, dirty, saving } = useSectionDraft("importing");
  if (!draft) return null;
  return (
    <SectionCard title="Импорт аккаунтов по умолчанию" subtitle="Префилл диалога импорта новых аккаунтов.">
      <Row label="Скорость прогрева">
        <SimpleSelect
          options={SPEED_OPTS}
          value={draft.default_warmup_speed}
          onValueChange={(v) => set({ default_warmup_speed: v as WarmupSpeed })}
        />
      </Row>
      <Row label="Тег-источник по умолчанию">
        <Input
          value={draft.default_source_label ?? ""}
          onChange={(e) => set({ default_source_label: e.target.value || null })}
        />
      </Row>
      <SaveBar dirty={dirty} saving={saving} onSave={save} />
    </SectionCard>
  );
}

/* ------------------------------------------------------------------- Proxy */

export function ProxySettingsCard() {
  const { draft, set, save, dirty, saving } = useSectionDraft("proxy");
  if (!draft) return null;
  return (
    <SectionCard title="Политика прокси">
      <Row label="Авто-проверка живости" hint="Фоновый воркер пере-проверяет прокси каждые N часов.">
        <Switch checked={draft.auto_health_check} onCheckedChange={(v) => set({ auto_health_check: !!v })} />
      </Row>
      <Row label="Интервал проверки, ч (1–168)">
        <NumField
          value={draft.health_check_interval_hours}
          onChange={(v) => set({ health_check_interval_hours: v ?? 6 })}
          min={1}
          max={168}
        />
      </Row>
      <Row
        label="Один аккаунт на прокси"
        hint="Если прокси перестал отвечать, его аккаунты сами переходят на рабочий прокси той же страны и того же типа. С этой галочкой — только на свободный: два аккаунта на одном прокси не окажутся. Автоперевод работает только при включённой авто-проверке живости; на импорт галочка не действует."
      >
        <Switch
          checked={draft.one_account_per_proxy}
          onCheckedChange={(v) => set({ one_account_per_proxy: !!v })}
        />
      </Row>
      <SaveBar dirty={dirty} saving={saving} onSave={save} />
    </SectionCard>
  );
}

/* ----------------------------------------------------------------- Inviter */

export function InviterSettingsCard() {
  const { draft, set, save, dirty, saving } = useSectionDraft("inviter");
  if (!draft) return null;
  return (
    <SectionCard title="Инвайтер по умолчанию" subtitle="Значения по умолчанию для кампаний.">
      <Row label="Стратегия по умолчанию">
        <SimpleSelect
          options={[
            { value: "manual_add", label: "Прямое добавление" },
            { value: "dm_message", label: "DM-сообщение" },
            { value: "channel_forward", label: "Форвард из канала" },
            { value: "story_mention", label: "Упоминание в истории" },
          ]}
          value={draft.default_strategy}
          onValueChange={(v) => set({ default_strategy: v as InviterStrategy })}
        />
      </Row>
      <Row
        label="Тихие часы рассылки"
        hint="Не слать DM/инвайты вне окна (по часовому поясу компании) — чтобы не писать незнакомцам в 4 утра. Кампания просто отложится до открытия окна."
      >
        <Switch
          checked={draft.outreach_quiet_hours_enabled}
          onCheckedChange={(v) => set({ outreach_quiet_hours_enabled: !!v })}
        />
      </Row>
      <Row
        label="Отправок на кампанию в час (10–600)"
        hint="Общий бюджет одной кампании на все её аккаунты. Защищает получателей: сотня аккаунтов, разом пишущая подписчикам одного канала, — это заметный почерк. Но это же и потолок скорости: 60/час ≈ 1440 в сутки, чего флоту от ~120 аккаунтов уже мало. Поднимайте вместе с флотом."
      >
        <NumField
          value={draft.campaign_sends_per_hour}
          min={10}
          max={600}
          onChange={(v) => set({ campaign_sends_per_hour: v ?? 60 })}
        />
      </Row>
      {draft.outreach_quiet_hours_enabled && (
        <>
          <Row label="Слать с часа (0–23)">
            <NumField
              value={draft.outreach_active_hour_start}
              min={0}
              max={23}
              onChange={(v) => set({ outreach_active_hour_start: v ?? 10 })}
            />
          </Row>
          <Row label="Слать до часа (1–24)">
            <NumField
              value={draft.outreach_active_hour_end}
              min={1}
              max={24}
              onChange={(v) => set({ outreach_active_hour_end: v ?? 22 })}
            />
          </Row>
        </>
      )}
      <SaveBar dirty={dirty} saving={saving} onSave={save} />
    </SectionCard>
  );
}
type InviterStrategy = "manual_add" | "dm_message" | "channel_forward" | "story_mention";

/* ------------------------------------------------------------------- Inbox */

export function InboxSettingsCard() {
  const { draft, set, save, dirty, saving } = useSectionDraft("inbox");
  if (!draft) return null;
  return (
    <SectionCard title="Входящие">
      <Row label="Частота опроса, мин (1–120)" hint="Как часто воркер тянет входящие из Telegram.">
        <NumField
          value={draft.poll_interval_minutes}
          onChange={(v) => set({ poll_interval_minutes: v ?? 5 })}
          min={1}
          max={120}
        />
      </Row>
      <SaveBar dirty={dirty} saving={saving} onSave={save} />
    </SectionCard>
  );
}

/* ----------------------------------------------------------- Notifications */

export function NotificationsCard() {
  const { draft, set, save, dirty, saving } = useSectionDraft("notifications");
  if (!draft) return null;
  return (
    <SectionCard
      title="Уведомления"
      subtitle="Алерты о банах, мёртвых прокси, завершении кампаний и т.п."
    >
      <Alert tone="info">
        Telegram-доставка: создай бота у @BotFather, вставь токен и свой Chat ID.
      </Alert>
      <Row label="Канал доставки">
        <SimpleSelect
          options={[
            { value: "none", label: "Выключено" },
            { value: "telegram", label: "Telegram-бот" },
          ]}
          value={draft.channel}
          onValueChange={(v) => set({ channel: v as "none" | "telegram" })}
        />
      </Row>
      {draft.channel === "telegram" && (
        <>
          <Row
            label="Токен бота"
            hint={draft.telegram_bot_token_set ? "Сохранён. Введите новый, чтобы заменить." : "Не задан."}
          >
            <Input
              type="password"
              value={draft.telegram_bot_token}
              placeholder={draft.telegram_bot_token_set ? "••••••" : "123:ABC…"}
              onChange={(e) => set({ telegram_bot_token: e.target.value })}
            />
          </Row>
          <Row label="Chat ID">
            <Input
              value={draft.telegram_chat_id ?? ""}
              onChange={(e) => set({ telegram_chat_id: e.target.value || null })}
            />
          </Row>
        </>
      )}
      <Row label="Аккаунт забанен / заморожен">
        <Switch checked={draft.on_account_banned} onCheckedChange={(v) => set({ on_account_banned: !!v })} />
      </Row>
      <Row label="Прокси умер">
        <Switch checked={draft.on_proxy_dead} onCheckedChange={(v) => set({ on_proxy_dead: !!v })} />
      </Row>
      <Row label="Кампания завершена">
        <Switch checked={draft.on_campaign_done} onCheckedChange={(v) => set({ on_campaign_done: !!v })} />
      </Row>
      <Row
        label="Кампания встала на паузу"
        hint="Несколько PeerFlood/FloodWait подряд — рассылка остановлена автоматически."
      >
        <Switch checked={draft.on_quota_exhausted} onCheckedChange={(v) => set({ on_quota_exhausted: !!v })} />
      </Row>
      <SaveBar dirty={dirty} saving={saving} onSave={save} />
    </SectionCard>
  );
}

/* ---------------------------------------------------------------- Security */

export function SecuritySettingsCard() {
  const { draft, set, save, dirty, saving } = useSectionDraft("security");
  if (!draft) return null;
  return (
    <SectionCard title="Безопасность">
      <Row label="Принудительная 2FA для операторов" hint="Без 2FA вход отправит оператора на её настройку.">
        <Switch checked={draft.enforce_2fa} onCheckedChange={(v) => set({ enforce_2fa: !!v })} />
      </Row>
      <SaveBar dirty={dirty} saving={saving} onSave={save} />
    </SectionCard>
  );
}

/* -------------------------------------------------------------------- Data */

export function DataSettingsCard() {
  const { draft, set, save, dirty, saving } = useSectionDraft("data");
  if (!draft) return null;
  return (
    <SectionCard title="Данные и хранение" subtitle="Аудитория хранится навсегда — нужна, чтобы не спамить одного юзера дважды.">
      <Row label="Чистить архив диалогов через N дней" hint="Пусто = не чистить. Чистится раз в сутки.">
        <NumField
          value={draft.auto_delete_archived_dialogs_days}
          min={1}
          max={3650}
          onChange={(v) => set({ auto_delete_archived_dialogs_days: v })}
          placeholder="∞"
        />
      </Row>
      <SaveBar dirty={dirty} saving={saving} onSave={save} />
    </SectionCard>
  );
}
